import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, FileText, MessageSquare, QrCode, Info } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDate } from "@/lib/format";
import { AdminModal, Field, Pill, textareaCls } from "@/pages/admin/ui";

const PAY_TYPE = { link: "Lien", manual: "Manuel (numéro)", qr: "QR Code", other: "Autre" };

function Detail({ label, value, mono }) {
  if (!value) return null;
  return (
    <div className="text-xs">
      <span className="text-slate-400">{label} : </span>
      <span className={`font-bold text-ink ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

export default function AdminVendorRequests() {
  const [tab, setTab] = useState("subscriptions");
  const [subs, setSubs] = useState([]);
  const [pays, setPays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respond, setRespond] = useState(null); // { req, action }
  const [message, setMessage] = useState("");
  const [activate, setActivate] = useState(true);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/vendor-subscription-requests").then((r) => setSubs(r.data)),
      api.get("/admin/vendor-payment-requests").then((r) => setPays(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const verifySub = async (id, action) => {
    try { await api.post(`/admin/vendor-subscription-requests/${id}/verify`, { action }); toast.success(action === "confirm" ? "Abonnement confirmé & activé" : "Demande refusée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openRespond = (req, action) => { setRespond({ req, action }); setMessage(""); setActivate(true); };
  const sendRespond = async () => {
    if (!respond) return;
    setSending(true);
    try {
      await api.post(`/admin/vendor-payment-requests/${respond.req.id}/respond`, { action: respond.action, message, activate });
      toast.success({ accept: "Moyen de paiement activé pour le revendeur", reject: "Demande refusée", info: "Demande d'informations envoyée" }[respond.action]);
      setRespond(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const tone = (s) => ({ confirmed: "green", approved: "green", rejected: "rose", info_requested: "amber" }[s] || "amber");
  const label = (s) => ({ confirmed: "Confirmé", approved: "Approuvé", rejected: "Refusé", info_requested: "Infos demandées", review: "En attente", pending: "En attente" }[s] || s);

  return (
    <div className="space-y-5" data-testid="admin-vendor-requests">
      <h1 className="font-display text-2xl font-extrabold text-ink">Demandes des revendeurs</h1>
      <div className="flex gap-2">
        <button onClick={() => setTab("subscriptions")} data-testid="tab-subscriptions" className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "subscriptions" ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>Abonnements ({subs.filter((s) => s.status === "review").length})</button>
        <button onClick={() => setTab("payments")} data-testid="tab-payments" className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "payments" ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>Moyens de paiement ({pays.filter((p) => p.status === "pending").length})</button>
      </div>

      {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div> : tab === "subscriptions" ? (
        !subs.length ? <Empty text="Aucune demande d'abonnement." /> : (
          <div className="space-y-3">
            {subs.map((r) => (
              <div key={r.id} data-testid={`sub-req-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white p-4">
                <div>
                  <p className="font-bold text-ink">{r.vendor_name} · {r.plan_name} <span className="text-brand">{formatPrice(r.price)}</span></p>
                  <p className="text-xs text-slate-500">Durée : {r.duration_days || "—"} jours · Moyen : <span className="font-bold text-ink">{r.payment_method || "—"}</span> · Réf/TXID : {r.transaction_id || "—"} · Tél : {r.phone || "—"}</p>
                  <p className="text-xs text-slate-400">{r.vendor_email} · {formatDate(r.created_at)}{r.submitted_at ? ` · payé le ${formatDate(r.submitted_at)}` : ""}</p>
                  {r.proof_path && <a href={fileUrl(r.proof_path)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand"><FileText className="h-3.5 w-3.5" /> Voir la preuve</a>}
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone={tone(r.status)}>{label(r.status)}</Pill>
                  {r.status === "review" && (<>
                    <button onClick={() => verifySub(r.id, "confirm")} data-testid={`sub-confirm-${r.id}`} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white"><Check className="h-3.5 w-3.5" /> Confirmer</button>
                    <button onClick={() => verifySub(r.id, "reject")} className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-bold text-rose-600"><X className="h-3.5 w-3.5" /> Refuser</button>
                  </>)}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        !pays.length ? <Empty text="Aucune demande de moyen de paiement." /> : (
          <div className="space-y-3">
            {pays.map((r) => (
              <div key={r.id} data-testid={`pay-req-${r.id}`} className="rounded-2xl border border-line bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-ink">{r.vendor_name} <span className="text-brand">· {r.methods?.join(", ") || r.method_name}</span></p>
                    <p className="text-xs text-slate-400">{r.vendor_email} · {formatDate(r.created_at)} · Type : {PAY_TYPE[r.payment_type] || r.payment_type || "—"}</p>
                    {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                    <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                      <Detail label="Numéro" value={r.payment_number} mono />
                      <Detail label="Titulaire" value={r.beneficiary_name} />
                      <Detail label="Réseau" value={r.network} />
                      <Detail label="Adresse" value={r.address} mono />
                      <Detail label="Lien" value={r.link} />
                    </div>
                    {r.extra_info && <p className="mt-1 text-xs text-slate-500">Infos : {r.extra_info}</p>}
                    {r.instructions && <p className="mt-1 text-xs italic text-slate-500">Instructions : {r.instructions}</p>}
                    {r.message && <p className="mt-1 text-xs italic text-slate-500">« {r.message} »</p>}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.logo_url && <a href={fileUrl(r.logo_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] font-bold text-brand"><FileText className="h-3 w-3" /> Logo</a>}
                      {r.image_url && <a href={fileUrl(r.image_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] font-bold text-brand"><FileText className="h-3 w-3" /> Image</a>}
                      {r.qr_image_url && <a href={fileUrl(r.qr_image_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] font-bold text-brand"><QrCode className="h-3 w-3" /> QR Code</a>}
                    </div>
                    {r.admin_reply && <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-slate-50 p-2 text-xs text-slate-600"><MessageSquare className="mt-0.5 h-3.5 w-3.5 text-brand" /> {r.admin_reply}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Pill tone={tone(r.status)}>{label(r.status)}</Pill>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <button onClick={() => openRespond(r, "accept")} data-testid={`pay-approve-${r.id}`} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white"><Check className="h-3.5 w-3.5" /> Valider</button>
                      <button onClick={() => openRespond(r, "info")} data-testid={`pay-info-${r.id}`} className="flex items-center gap-1.5 rounded-lg border border-sky-200 px-3 py-2 text-xs font-bold text-sky-600"><Info className="h-3.5 w-3.5" /> Infos</button>
                      <button onClick={() => openRespond(r, "reject")} data-testid={`pay-reject-${r.id}`} className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-bold text-rose-600"><X className="h-3.5 w-3.5" /> Refuser</button>
                      <button onClick={() => openRespond(r, "info")} data-testid={`pay-respond-${r.id}`} className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink"><MessageSquare className="h-3.5 w-3.5" /> Répondre</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      <AdminModal open={!!respond} onOpenChange={() => setRespond(null)}
        title={respond ? { accept: "Valider et activer le moyen", reject: "Refuser la demande", info: "Demander des informations" }[respond.action] : ""}>
        <div className="space-y-4">
          {respond && (
            <p className="text-sm text-slate-500">
              Revendeur : <b className="text-ink">{respond.req.vendor_name}</b> — {respond.req.methods?.join(", ") || respond.req.method_name}
            </p>
          )}
          <Field label="Message au revendeur">
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className={textareaCls} data-testid="respond-message"
              placeholder="Expliquez au revendeur ce qui doit être corrigé, ou confirmez l'activation…" />
          </Field>
          {respond?.action === "accept" && (
            <label className="flex items-center gap-2 text-sm font-bold text-ink">
              <input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} className="h-4 w-4 accent-brand" data-testid="respond-activate" />
              Activer immédiatement ce moyen de paiement dans le compte du revendeur
            </label>
          )}
          <button onClick={sendRespond} disabled={sending} data-testid="respond-send"
            className="w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {sending ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Envoyer la réponse"}
          </button>
        </div>
      </AdminModal>
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">{text}</div>;
